import { curry, divide, flip, multiply } from 'rambdax';
import { DEFAULT_LOCALES } from '@/translations/locales';

const defaultFormatterProps = {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
};

// Creates currency formatters
const currencyFormatter = new Intl.NumberFormat(
  DEFAULT_LOCALES,
  defaultFormatterProps,
);

const fractionCurrencyFormatter = new Intl.NumberFormat(DEFAULT_LOCALES, {
  ...defaultFormatterProps,
  minimumFractionDigits: 2,
});

/**
 * A helper util which converts cents to dollars
 *
 * @param {any} value cents
 * @returns {number} dollars
 */
const convertCentsToDollars = (value) => Math.round(Number(value)) * 0.01 || 0;

/**
 * formats a numeric value to be a string with the following format: $2,500 or $2,500.25
 *
 * @param {any} [val=0] value to be formatted
 * @param {boolean} [areCents=false] used to convert to dollars if necessary
 * @returns {string} formatted value
 */
const formatCurrency = (val = '0', areCents = false) => {
  let value = Number(val) || 0;

  if (areCents) {
    value = convertCentsToDollars(value);
  }

  return value % 1 === 0
    ? currencyFormatter.format(value)
    : fractionCurrencyFormatter.format(value);
};

/**
 * @param {any} [val=0] value to be formatted
 * @param {boolean} [areCents=false] used to convert to dollars if necessary
 * @returns {string} formatted value
 */
const formatCurrencyWithDecimals = (val = 0, areCents = false) => {
  let value = Number(val);

  if (areCents) {
    value = convertCentsToDollars(value);
  }

  return fractionCurrencyFormatter.format(value);
};

// Method helps with formatting string from NumberInput
// Check for typeof as if the field is left unaltered, the value in state will
// remain a number, changing input creates a string in state which needs conversion
const formatCurrencyInputToCents = (value) => {
  if (typeof value === 'number') {
    return Math.round(value * 100);
  }

  let formattedCurrency;

  try {
    // Format target amount to a number in cents for submission
    formattedCurrency = Number(
      (parseFloat(value.replace(/\$/g, '').replace(/,/g, '')) * 100).toFixed(0),
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Could not format currency', e);
    return null;
  }

  return formattedCurrency;
};

const multiply60 = multiply(60);
const multiply100 = multiply(100);
const divide60 = curry(flip(divide))(60);
const divide100 = curry(flip(divide))(100);

export {
  convertCentsToDollars,
  currencyFormatter,
  divide100,
  divide60,
  formatCurrency,
  formatCurrencyInputToCents,
  formatCurrencyWithDecimals,
  fractionCurrencyFormatter,
  multiply100,
  multiply60,
};
